<template>
  <vs-popup class="customer-project-brief-create-pop-up"
            id="popup"
            title="CREATE CUSTOMER PROJECT BRIEF"
            @close="open = false"
            :active.sync="open"
            :buttons-hidden="true">
    <vx-card no-shadow>

      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form id="create-project-brief-pop-up" @submit.prevent="handleSubmit(createProjectBrief)" autocomplete="off">

          <!-- Row 1: Identity -->
          <vx-card cardBorder no-shadow>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0" v-if="isCreateFromMainMenu">
                <ValidationProvider vid="customer_id" name="customer_id" rules="required" v-slot="{ errors }">
                  <small class="label">Customer <span class="text-danger font-bold">*</span></small>
                  <div :class="{'input-icon-validate-danger': errors[0]}">
                    <v-select @search="onSearch" :clearable="true" name="customer_id" placeholder=""
                              label="company_name" v-model="createForm.customer_id" :reduce="option => option.id"
                              :options="customers" :dir="$vs.rtl ? 'rtl' : 'ltr'" @input="fetchCustomerUsers">
                      <template slot="no-options">type to search customer</template>
                    </v-select>
                  </div>
                  <div v-if="errors[0]" class="con-text-validation span-text-validation-danger vs-input--text-validation-span"><span class="span-text-validation">{{ errors[0] }}</span></div>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="project_type" name="project_type" rules="" v-slot="{ errors }">
                  <small class="label">Project Type</small>
                  <v-select name="project_type" v-model="createForm.project_type" :options="projectTypeOptions" :dir="$vs.rtl ? 'rtl' : 'ltr'" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="name" name="name" rules="required" v-slot="{ errors }">
                  <small class="label">Project Brief Name <span class="text-danger font-bold">*</span></small>
                  <vs-input v-model="createForm.name" name="name" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
            </div>

            <!-- Row 2: Industry Sector | Product Category | Date Required -->
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="industry_sector" name="industry_sector" rules="" v-slot="{ errors }">
                  <small class="label">Industry Sector</small>
                  <v-select multiple v-on:input="onChangeIndustrySector" :clearable="false" :closeOnSelect="false" name="industry_sector" placeholder="" label="name"
                            v-model="createForm.industry_sector" :reduce="option => option.id" :options="industrySectors" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                    <template v-slot:option="option"><span>{{option.name}}</span></template>
                  </v-select>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="industry_sub_sector" name="industry_sub_sector" rules="" v-slot="{ errors }">
                  <small class="label">Product Category</small>
                  <v-select multiple :clearable="false" :closeOnSelect="false" name="industry_sub_sector" placeholder="" label="name"
                            v-model="createForm.industry_sub_sector" :reduce="option => option.id" :options="industrySubSectors" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                    <template v-slot:option="option"><span>{{option.name}}</span></template>
                    <template #no-options>Please select Industry Sector first!</template>
                  </v-select>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="date_required" name="date_required" rules="" v-slot="{ errors }">
                  <small class="label">Date Required</small>
                  <flat-pickr style="display: none" class="w-full" :config="{ dateFormat: 'd/m/Y'}" v-model="createForm.date_required" placeholder="Choose Date"/>
                  <flat-pickr class="w-full" :config="{ dateFormat: 'd/m/Y'}" v-model="createForm.date_required" placeholder="Choose Date"/>
                </ValidationProvider>
              </div>
            </div>

            <!-- Row 3: I.T.S. Sales Lead | Customer Contact | Assigned I.T.S. Staff -->
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="sales_lead_staff_id" name="sales_lead_staff_id" rules="" v-slot="{ errors }">
                  <small class="label">I.T.S. Sales Lead</small>
                  <v-select @search="onSearchSalesStaff" :clearable="true" name="sales_lead_staff_id" placeholder=""
                            label="name" v-model="createForm.sales_lead_staff_id" :reduce="option => option.id"
                            :options="sales_staffs" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                    <template slot="no-options">type to search staff</template>
                  </v-select>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="customer_user" name="customer_user" rules="" v-slot="{ errors }">
                  <small class="label">Customer Contact</small>
                  <vs-input v-model="createForm.customer_user" name="customer_user" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="assigned_staff_id" name="assigned_staff_id" rules="" v-slot="{ errors }">
                  <small class="label">Assigned I.T.S. Staff</small>
                  <v-select @search="onSearchAssignedStaff" :clearable="true" multiple :closeOnSelect="false" name="assigned_staff_ids" placeholder=""
                            label="name" v-model="createForm.assigned_staff_ids" :reduce="option => option.id"
                            :options="assigned_staffs" :dir="$vs.rtl ? 'rtl' : 'ltr'">
                    <template slot="no-options">type to search staff</template>
                  </v-select>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>

            <!-- Row 4: Sample Delivery Address Site Select -->
            <div class="vx-row mb-3">
              <div class="vx-col w-full">
                <small class="label">Sample Delivery Address (Select Site)</small>
                <v-select name="delivery_address_select" placeholder="Select a site to pre-fill address"
                          v-model="selectedDeliveryAddress"
                          label="name" :options="addresses" :dir="$vs.rtl ? 'rtl' : 'ltr'"
                          @input="onChangeDeliveryAddress">
                  <template slot="option" slot-scope="option">
                    <span>{{ option.name }}<span v-if="option.line1">, {{ option.line1 }}</span><span v-if="option.town_state">, {{ option.town_state }}</span><span v-if="option.post_code">, {{ option.post_code }}</span></span>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <span>{{ option.name }}<span v-if="option.line1">, {{ option.line1 }}</span><span v-if="option.town_state">, {{ option.town_state }}</span><span v-if="option.post_code">, {{ option.post_code }}</span></span>
                  </template>
                </v-select>
              </div>
            </div>

            <!-- Row 5: Sample Delivery Address Textarea -->
            <div class="vx-row mb-3">
              <div class="vx-col w-full">
                <small class="label">Sample Delivery Address</small>
                <vs-textarea class="mb-0" v-model="createForm.sample_delivery_address" height="80px" />
              </div>
            </div>
          </vx-card>

          <!-- Flavour Status | Physical State + Halal | Kosher | Organic -->
          <vx-card cardBorder class="mt-4" no-shadow>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/2 w-full md:mb-0">
                <ValidationProvider vid="natural_synthetic" name="natural_synthetic" rules="" v-slot="{ errors }">
                  <small class="label">Flavour Status</small>
                  <v-select name="natural_synthetic" v-model="createForm.natural_synthetic" :options="['Natural Flavouring', 'Natural &quot;X&quot; Flavouring', 'Natural Flavouring WONF', 'Synthetic Flavouring']" :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/2 w-full md:mb-0">
                <ValidationProvider vid="liquid_powder" name="liquid_powder" rules="" v-slot="{ errors }">
                  <small class="label">Physical State</small>
                  <v-select multiple :closeOnSelect="false" name="liquid_powder" v-model="createForm.liquid_powder" :options="['Liquid', 'Powder', 'Spray Dry Powder', 'Fruit']" :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="halal" name="halal" rules="" v-slot="{ errors }">
                  <small class="label">Halal</small>
                  <v-select name="halal" v-model="createForm.halal" :options="['Halal Suitable', 'Halal Certified', 'N/A']" :dir="$vs.rtl ? 'rtl' : 'ltr'" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="kosher" name="kosher" rules="" v-slot="{ errors }">
                  <small class="label">Kosher</small>
                  <v-select name="kosher" v-model="createForm.kosher" :options="['Kosher Suitable', 'Kosher Certified', 'N/A']" :dir="$vs.rtl ? 'rtl' : 'ltr'" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="organic" name="organic" rules="" v-slot="{ errors }">
                  <small class="label">Organic</small>
                  <v-select name="organic" v-model="createForm.organic" :options="['Organic Suitable', 'Organic Certified', 'N/A']" :dir="$vs.rtl ? 'rtl' : 'ltr'" />
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="specification_match_required" name="specification_match_required" rules="" v-slot="{ errors }">
                  <small class="label">Specification Match Required</small>
                  <v-select name="specification_match_required" v-model="createForm.specification_match_required"
                            :options="['Yes', 'No', 'N/A']" :dir="$vs.rtl ? 'rtl' : 'ltr'"/>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="specification_supplied" name="specification_supplied" :rules="createForm.project_type === 'Flavour Spec Match' ? 'required' : ''" v-slot="{ errors }">
                  <small class="label">Specification Supplied <span v-if="createForm.project_type === 'Flavour Spec Match'" class="text-danger font-bold">*</span></small>
                  <div :class="{'input-icon-validate-danger': errors[0]}">
                    <v-select name="specification_supplied" v-model="createForm.specification_supplied" :options="['Yes', 'No', 'N/A']" :dir="$vs.rtl ? 'rtl' : 'ltr'" />
                  </div>
                  <div v-if="errors[0]" class="con-text-validation span-text-validation-danger vs-input--text-validation-span"><span class="span-text-validation">{{ errors[0] }}</span></div>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="flavour_supplied" name="flavour_supplied" :rules="createForm.project_type === 'Flavour Spec Match' ? 'required' : ''" v-slot="{ errors }">
                  <small class="label">Target Flavour Supplied <span v-if="createForm.project_type === 'Flavour Spec Match'" class="text-danger font-bold">*</span></small>
                  <vs-input v-model="createForm.flavour_supplied" name="flavour_supplied" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="target_selling_price" name="target_selling_price" :rules="createForm.project_type === 'Flavour Spec Match' ? 'required' : ''" v-slot="{ errors }">
                  <small class="label">Target Selling Price (£/Kg) <span v-if="createForm.project_type === 'Flavour Spec Match'" class="text-danger font-bold">*</span></small>
                  <vs-input v-model="createForm.target_selling_price" name="target_selling_price" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="target_raw_material_price" name="target_raw_material_price" rules="" v-slot="{ errors }">
                  <small class="label">Target Raw Material Price (£/Kg)</small>
                  <vs-input v-model="createForm.target_raw_material_price" name="target_raw_material_price" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="value_to_company" name="value_to_company" rules="number" v-slot="{ errors }">
                  <small class="label">Value to I.T.S. (£/Yr)</small>
                  <vs-input v-model="createForm.value_to_company" name="value_to_company" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="volume" name="volume" rules="number" v-slot="{ errors }">
                  <small class="label">Volume (Kg/Yr)</small>
                  <vs-input v-model="createForm.volume" name="volume" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-2/3 w-full md:mb-0">
                <ValidationProvider vid="formulations" name="formulations" rules="" v-slot="{ errors }">
                  <small class="label">Formulations Created and/or Supplied</small>
                  <v-select name="formulations" @search="onSearchFormulation" v-model="createForm.formulations"
                            :options="formulations" :dir="$vs.rtl ? 'rtl' : 'ltr'" multiple label="formulation_code">
                    <template v-slot:option="option"><span>{{option.formulation_code}} - {{option.name}}</span></template>
                    <template slot="selected-option" slot-scope="option"><span>{{option.formulation_code}}</span></template>
                    <template slot="no-options">type to search formulation</template>
                  </v-select>
                  <span class="text-danger text-sm">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </vx-card>

          <!-- Notes -->
          <vx-card cardBorder class="mt-4" no-shadow>
            <div class="vx-row mb-3">
              <div class="vx-col w-full">
                <ValidationProvider vid="description" name="description" rules="required" v-slot="{ errors }">
                  <small class="label">Description and Project Notes <span class="text-danger font-bold">*</span></small>
                  <vs-textarea class="mb-0" v-model="createForm.description" height="100px" />
                  <span class="text-danger text-xs mt-0">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col w-full">
                <ValidationProvider vid="note" name="note" rules="" v-slot="{ errors }">
                  <small class="label">I.T.S. Private Notes on Project Brief</small>
                  <vs-textarea class="mb-0" v-model="createForm.note" height="100px" />
                  <span class="text-danger text-xs mt-0">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </vx-card>

          <!-- For NPD Use -->
          <vx-card title="For NPD Use" cardBorder class="mt-4" no-shadow>
            <div class="vx-row mb-3">
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="new_rm_used" name="new_rm_used" rules="" v-slot="{ errors }">
                  <small class="label">New Raw Material Used</small>
                  <vs-input v-model="createForm.new_rm_used" name="new_rm_used" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
              <div class="vx-col md:w-1/3 w-full md:mb-0">
                <ValidationProvider vid="new_supplier_used" name="new_supplier_used" rules="" v-slot="{ errors }">
                  <small class="label">New Supplier Used</small>
                  <vs-input v-model="createForm.new_supplier_used" name="new_supplier_used" class="w-full" :danger="!!errors[0]" :danger-text="errors[0]" val-icon-danger="icon-x" val-icon-pack="feather"/>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-row mb-3">
              <div class="vx-col w-full">
                <ValidationProvider vid="npd_notes" name="npd_notes" rules="" v-slot="{ errors }">
                  <small class="label">NPD Notes</small>
                  <vs-textarea class="mb-0" v-model="createForm.npd_notes" height="100px" />
                  <span class="text-danger text-xs mt-0">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </vx-card>

          <!-- Save & Reset Button -->
          <div class="flex flex-wrap items-center justify-end mt-8">
            <vs-button button="submit" class="ml-auto mt-2">Save Changes</vs-button>
            <vs-button @click="createForm = {}" class="ml-4 mt-2" type="border" color="warning">Reset</vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
  import { RepositoryFactory } from '@/repository/RepositoryFactory'
  const CustomerRepository = RepositoryFactory.get('customer')
  const StaffRepository = RepositoryFactory.get('staff')
  const SupplierManufactureRepository = RepositoryFactory.get('supplierManufacture');
  const SupplierRawMaterialRepository = RepositoryFactory.get('supplierRawMaterial');
  const SupplierRepository = RepositoryFactory.get('supplier');
  const DropDownRepository = RepositoryFactory.get('drop_down')

  import flatPickr from 'vue-flatpickr-component'
  import 'flatpickr/dist/flatpickr.css'
  import vSelect from 'vue-select'
  import SuccessMessage from '@/mixins/SuccessMessage'
  import ErrorMessages from '@/mixins/ErrorMessages'

  function initialState() {
    return {
      open: false,
      id: '',
      createForm: {
        new_rm_used: '',
        new_supplier_used: '',
        industry_sector: [],
        industry_sub_sector: [],
        formulations: [],
        assigned_staff_ids: []
      },
      countryCodes: [],
      customerUsers: [],
      isCreateFromMainMenu: false,
      customers: [],
      assigned_staffs: [],
      sales_staffs: [],
      raw_material_list: [],
      supplier_list: [],
      industrySectors: [],
      industrySubSectors: [],
      customer_users_list: [],
      formulations: [],
      addresses: [],
      selectedDeliveryAddress: null,
      projectTypeOptions: [
        'Application match',
        'Carrier change',
        'Compound',
        'Cost reduction',
        'Flavour Application',
        'Flavour match',
        'Flavour Off-Shelf match',
        'Flavour Spec Match',
        'Large Sample Request',
        'Masking Flavour',
        'New Flavour Creation',
        'Range of samples',
        'Other (see notes)',
      ],
    }
  }

export default {
  name: 'CreateCustomerProjectBriefPopUp',
  data () {
    return initialState()
  },
  methods:{
    async openPopup (id) {
      try {
        Object.assign(this.$data, initialState())
        this.$refs.form.reset();
        this.open = true;
        if (!!id) {
          this.id = id
          this.isCreateFromMainMenu = false
          await this.loadAddresses(id)
        } else{
          this.isCreateFromMainMenu = true
        }
        await this.getRawMaterialList()
        await this.getSupplierList()
        await this.loadIndustrySectors()
      } catch (error) {

      }
    },
    async createProjectBrief () {
      const vue = this;
      this.$vs.loading()
       try {
         const { ...payload } = this.createForm
         if (!this.isCreateFromMainMenu) {
           payload.customer_id = this.id
         }
         if(payload.formulations.length > 0) {
           payload.formulations = payload.formulations.map(item => item.id)
         }
         await CustomerRepository.createCustomerProjectBrief(payload)
         this.showSuccessMessage('Project brief created successfully')
         await vue.$parent.refreshData()
         this.open = false;
       }catch (e) {
         this.$vs.loading.close()
         this.convertAndNotifyError(e)
       }
      this.$vs.loading.close()
    },
    async getRawMaterialList() {
      this.$vs.loading({
        container: '#create-project-brief-pop-up',
        scale: 0.45
      })
      const {data} = await SupplierRawMaterialRepository.getRawMaterialAcceptedList(1, '', 100, '')
      this.raw_material_list =  data.data
      this.$vs.loading.close('#create-project-brief-pop-up > .con-vs-loading')
    },
    async getSupplierList() {
      this.$vs.loading({
        container: '#create-project-brief-pop-up',
        scale: 0.45
      })
      const {data} = await DropDownRepository.getApprovedSupplierList()
      this.supplier_list =  data.data
      this.$vs.loading.close('#create-project-brief-pop-up > .con-vs-loading')
    },
    async loadIndustrySectors() {
      const {data} =  (await CustomerRepository.getIndustrySectors()).data
      this.industrySectors =  data
    },
    async onChangeIndustrySector(ids) {

      await this.$vs.loading();
      try {
        this.createForm.industry_sub_sector = []
        const { data } =  (await CustomerRepository.getIndustrySubSectors({ ids: ids })).data
        this.industrySubSectors =  data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      await this.$vs.loading.close()
    },
    onSearch(search, loading) {
      if(search.length) {
        loading(true);
        this.search(loading, search, this);
      }
    },
    onSearchAssignedStaff(search, loading) {
      if(search.length) {
        loading(true);
        this.searchStaff(loading, search, this, 'assigned');
      }
    },
    onSearchSalesStaff(search, loading) {
      if(search.length) {
        loading(true);
        this.searchStaff(loading, search, this, 'sales');
      }
    },
    onSearchFormulation(search, loading) {
      if(search.length) {
        loading(true);
        this.searchFormulation(loading, search, this);
      }
    },
    async fetchCustomerUsers() {
      // const { data } = (await DropDownRepository.getCustomerUsersDropdownList(this.createForm.customer_id)).data
      // this.customer_users_list = data
      if (this.createForm.customer_id) {
        await this.loadAddresses(this.createForm.customer_id)
      }
    },
    search: _.debounce( async (loading, search, vm) => {
      const {data} = await  DropDownRepository.getCustomersList(`filter[search]=${search}`)
      vm.customers = data.data
      loading(false);
    }, 350),
    searchStaff: _.debounce( async (loading, search, vm, type) => {
      const {data} = await DropDownRepository.getStaffList(`filter[search]=${search}`)
      if(type === 'assigned') {
        const kept = vm.assigned_staffs.filter(s => vm.createForm.assigned_staff_ids.includes(s.id))
        vm.assigned_staffs = [...kept, ...data.data.filter(s => !vm.createForm.assigned_staff_ids.includes(s.id))]
      }
      if(type === 'sales') {
        vm.sales_staffs = data.data
      }
      loading(false);
    }, 350),
    searchFormulation: _.debounce( async (loading, search, vm) => {
      const {data} = await DropDownRepository.getFormulationList(`filter[search]=${search}`)
      vm.formulations = data.data
      loading(false);
    }, 350),
    async loadAddresses(customerId) {
      try {
        const raw = (await CustomerRepository.getSampleDeliveryAddresses(customerId)).data.data
        this.addresses = raw.map(addr => ({
          ...addr,
          id: addr.type === 'Main Address' ? 'main' : addr.type === 'Invoice Address' ? 'invoice' : String(addr.id),
        }))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    onChangeDeliveryAddress(selected) {
      if (selected) {
        if (selected.type === 'Main Address') {
          this.createForm.delivery_address_id = 'main'
        } else if (selected.type === 'Invoice Address') {
          this.createForm.delivery_address_id = 'invoice'
        } else {
          this.createForm.delivery_address_id = String(selected.id)
        }
      }
    },
    myFilter: (option, label, search) => {
      let temp = search.toLowerCase();
      return option.name.toLowerCase().indexOf(temp) > -1 ||
        option.formulation_code.toLowerCase().indexOf(temp) > -1
    },
  },
  async mounted () {

  },
  components: {
    'v-select': vSelect,
    flatPickr,
  },
  mixins: [SuccessMessage, ErrorMessages],
}
</script>

<style scoped>
  .input-icon-validate-danger >>> .vs__dropdown-toggle {
    border: 1px solid rgba(var(--vs-danger),1) !important;
  }

  .customer-project-brief-create-pop-up >>> .vs-popup {
    /*min-height: 50% !important;*/
    min-width: 60% !important;
  }

</style>
